import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Redirect } from 'react-router-dom';
import Switch from 'react-switch';
import Loading from '../ui/Loading.js';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Utils from '../../commons/utils/Utils';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

const BASE_URL = '/inventario/mano-obra';

/**
 * VEC-3432 — ABM del catálogo de Mano de Obra.
 *
 * `tipo` es fijo "Mano De Obra" y de solo lectura (Scenario 03): este catálogo es el de mano de
 * obra y el de repuestos es VEC-3312. Tampoco se muestran los campos exclusivos de repuestos —
 * GTIN, unidad de medida, marca, modelo y stock mínimo—, que el backend no acepta para este tipo.
 */
const TIPO_MANO_DE_OBRA = 'Mano De Obra';

/** Obligatorios del alta (Scenario 01). La mano de obra no tiene unidad ni stock mínimo. */
const CAMPOS_REQUERIDOS = ['numeroDeParte', 'descripcion', 'categoria', 'subcategoria'];

const FORM_INIT = {
  id: 0,
  numeroDeParte: '',
  descripcion: '',
  categoria: '',
  subcategoria: '',
  referencia: '',
  costo: '',
  valorMaximo: '',
  costoFijo: false,
  activo: true,
};

const InventarioManoObraAbm = (props) => {
  const intl = useIntl();
  const [redirectTo, setRedirectTo] = useState(null);
  const [formData, setFormData] = useState({ ...FORM_INIT });
  const [loading, setLoading] = useState(false);

  /**
   * Los valores con los que se cargó el ítem. Sirven para distinguir "este campo ya venía vacío"
   * de "el usuario lo borró": ver validate() y armarPayload().
   */
  const [valoresIniciales, setValoresIniciales] = useState(null);

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);

  const isView = props.action === 'VIEW';
  const isEdit = props.action === 'EDIT';

  useEffect(() => {
    if (
      ((Security.hasPermission('INVENTARIO_MANO_OBRA_CREAR') && props.action === 'ADD') ||
        (Security.hasPermission('INVENTARIO_MANO_OBRA_MODIFICAR') && props.action === 'EDIT') ||
        (Security.hasPermission('INVENTARIO_MANO_OBRA_VISUALIZAR') && props.action === 'VIEW')) &&
      ConfigBusiness.get('inventarioItems.habilitado') === 'true'
    ) {
      initSelects();
      initForm();
    } else {
      setRedirectTo('/error');
    }
  }, [props.action, props.match && props.match.params && props.match.params.id]);

  /**
   * El combo de categorías usa el endpoint propio del módulo, que devuelve SOLO las que tienen al
   * menos una subcategoría. Con la lista completa, elegir una categoría sin hijos deja el
   * formulario imposible de completar: el combo de subcategorías queda vacío y ambas son
   * obligatorias. Las subcategorías sí se piden a Pañol2, que es de solo lectura.
   */
  const initSelects = () => {
    Utils.getData(`${BASE_URL}/select/categorias`)
      .then((data) => setCategorias(data || []))
      .catch(() => setCategorias([]));
  };

  const fetchSubcategorias = (categoriaId) => {
    if (!categoriaId) {
      setSubcategorias([]);
      return;
    }
    Utils.getData('/panol2/select/subcategorias?categoria=' + categoriaId)
      .then((data) => setSubcategorias(data || []))
      .catch(() => setSubcategorias([]));
  };

  const initForm = () => {
    if (props.action === 'ADD') {
      setValoresIniciales(null);
      return;
    }
    setLoading(true);
    Utils.getData(`${BASE_URL}/${props.match.params.id}`)
      .then((data) => {
        if (data) {
          const cargado = {
            id: data.id,
            numeroDeParte: data.numeroDeParte !== null && data.numeroDeParte !== undefined ? data.numeroDeParte : '',
            descripcion: data.descripcion !== null && data.descripcion !== undefined ? data.descripcion : '',
            categoria: data.categoriaId !== null && data.categoriaId !== undefined ? data.categoriaId : '',
            subcategoria: data.subcategoriaId !== null && data.subcategoriaId !== undefined ? data.subcategoriaId : '',
            referencia: data.referencia !== null && data.referencia !== undefined ? data.referencia : '',
            costo: data.costo !== null && data.costo !== undefined ? data.costo : '',
            valorMaximo: data.valorMaximo !== null && data.valorMaximo !== undefined ? data.valorMaximo : '',
            costoFijo: data.costoFijo ? true : false,
            activo: data.activo ? true : false,
          };
          setFormData(cargado);
          setValoresIniciales(cargado);
          fetchSubcategorias(data.categoriaId);
        } else {
          exit();
        }
      })
      .catch(() => exit())
      .finally(() => setLoading(false));
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoriaChange = (value) => {
    setFormData((prev) => ({ ...prev, categoria: value, subcategoria: '' }));
    fetchSubcategorias(value);
  };

  const vacio = (valor) => valor === '' || valor === null || valor === undefined;

  /** Un campo que ya venía vacío del servidor: es el caso de los ítems cargados por el ABM legacy. */
  const yaVeniaVacio = (name) => !!valoresIniciales && vacio(valoresIniciales[name]);

  // Los mensajes se resuelven con `id` literal para que `formatjs extract` los pueda tomar:
  // pasarle el id como variable los dejaría fuera del catálogo y sin poder traducirse.
  const warn = (message) => {
    Swal.fire({ icon: 'warning', title: message, confirmButtonText: 'OK' });
  };

  const labelDeCampo = (name) => {
    switch (name) {
      case 'numeroDeParte':
        return intl.formatMessage({ id: 'inventarioManoObra.error.numero_de_parte_required', defaultMessage: 'El número de parte es requerido' });
      case 'descripcion':
        return intl.formatMessage({ id: 'inventarioManoObra.error.descripcion_required', defaultMessage: 'La descripción es requerida' });
      case 'categoria':
        return intl.formatMessage({ id: 'inventarioManoObra.error.categoria_required', defaultMessage: 'La categoría es requerida' });
      default:
        return intl.formatMessage({ id: 'inventarioManoObra.error.subcategoria_required', defaultMessage: 'La subcategoría es requerida' });
    }
  };

  /**
   * En el ALTA los cuatro obligatorios tienen que estar.
   *
   * En la EDICIÓN se exige solo lo que el ítem ya tenía: los ítems de mano de obra cargados por el
   * ABM legacy no tienen categoría, subcategoría ni número de parte, porque ese ABM nunca los pidió.
   * Si acá se exigieran igual, corregirle el costo a uno de esos ítems obligaría a inventarle los
   * datos que faltan — y el backend, que aplica semántica de patch, los aceptaría sin problema. Sí
   * se rechaza vaciar un campo que SÍ tenía valor: eso es un borrado, no un dato que nunca estuvo.
   */
  const validate = () => {
    for (let i = 0; i < CAMPOS_REQUERIDOS.length; i++) {
      const name = CAMPOS_REQUERIDOS[i];
      const valor = formData[name];
      const estaVacio = typeof valor === 'string' ? !valor.trim() : vacio(valor);

      if (!estaVacio) continue;
      if (isEdit && yaVeniaVacio(name)) continue;

      warn(labelDeCampo(name));
      return false;
    }

    if (formData.costo !== '' && (isNaN(Number(formData.costo)) || Number(formData.costo) < 0)) {
      warn(intl.formatMessage({ id: 'inventarioManoObra.error.costo_invalido', defaultMessage: 'El costo debe ser un número mayor o igual a cero' }));
      return false;
    }
    if (formData.valorMaximo !== '' && isNaN(Number(formData.valorMaximo))) {
      warn(intl.formatMessage({ id: 'inventarioManoObra.error.valor_maximo_invalido', defaultMessage: 'El valor máximo debe ser un número' }));
      return false;
    }
    // VEC-3498: se avisa acá para no hacer ir y volver al servidor, pero la autoridad es el
    // backend (ItemService::validarCostoNoSuperaValorMaximo), que valida igual.
    if (
      formData.valorMaximo !== '' &&
      Number(formData.valorMaximo) > 0 &&
      formData.costo !== '' &&
      Number(formData.costo) > Number(formData.valorMaximo)
    ) {
      warn(
        intl.formatMessage(
          {
            id: 'inventarioManoObra.error.costo_supera_valor_maximo',
            defaultMessage: 'El costo supera el valor máximo permitido ({max}).',
          },
          { max: formData.valorMaximo }
        )
      );
      return false;
    }
    return true;
  };

  /**
   * Los obligatorios que siguen vacíos en una edición NO se mandan, para que el backend conserve
   * lo que tiene en vez de recibir un null y borrarlo. Es la contracara de validate().
   */
  const armarPayload = () => {
    const payload = {
      referencia: formData.referencia !== '' ? String(formData.referencia).trim() : null,
      costo: formData.costo !== '' ? Number(formData.costo) : 0,
      // VEC-3498: vacío = sin tope. El backend colapsa vacío/0/negativo a null.
      valorMaximo: formData.valorMaximo !== '' ? Number(formData.valorMaximo) : null,
      costoFijo: formData.costoFijo,
      activo: formData.activo,
    };

    if (props.action === 'ADD') {
      payload.tipo = TIPO_MANO_DE_OBRA;
    }

    payload.numeroDeParte = String(formData.numeroDeParte).trim();
    payload.descripcion = String(formData.descripcion).trim();
    payload.categoria = formData.categoria;
    payload.subcategoria = formData.subcategoria;

    if (isEdit) {
      CAMPOS_REQUERIDOS.forEach((name) => {
        const valor = formData[name];
        const estaVacio = typeof valor === 'string' ? !valor.trim() : vacio(valor);
        if (estaVacio) delete payload[name];
      });
    }

    // Sin permiso de desactivación el estado no se manda: lo conserva el backend.
    if (isEdit && !Security.hasPermission('INVENTARIO_MANO_OBRA_DESACTIVAR')) {
      delete payload.activo;
    }

    return payload;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);

    Utils.sendData(
      `${BASE_URL}${props.action !== 'ADD' ? '/' + formData.id : ''}`,
      props.action === 'ADD' ? 'POST' : 'PUT',
      armarPayload()
    )
      .then((response) => {
        if (response) exit();
      })
      // Utils.sendData ya muestra el error por Swal; acá solo se corta la propagación.
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const exit = () => {
    setRedirectTo(BASE_URL);
  };

  const labelOf = (options, value) => {
    for (let i = 0; i < options.length; i++) {
      if (String(options[i].value) === String(value)) return options[i].label;
    }
    return '-';
  };

  const renderText = (name, label, required) => (
    <div className="col-md-6">
      <div className="form-group row">
        <label className="col-md-4 label-control col-form-label" htmlFor={name}>
          {label}
          {required ? '*' : ''}:
        </label>
        <div className="col-md-8">
          {isView ? (
            <div className="form-control-static col-form-label form-value">
              {formData[name] !== '' && formData[name] !== null ? formData[name] : '-'}
            </div>
          ) : (
            <input
              type="text"
              className="form-control"
              id={name}
              name={name}
              value={formData[name]}
              onChange={(e) => handleInputChange(name, e.target.value)}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderNumber = (name, label, required) => (
    <div className="col-md-6">
      <div className="form-group row">
        <label className="col-md-4 label-control col-form-label" htmlFor={name}>
          {label}
          {required ? '*' : ''}:
        </label>
        <div className="col-md-8">
          {isView ? (
            <div className="form-control-static col-form-label form-value">
              {formData[name] !== '' && formData[name] !== null ? formData[name] : '-'}
            </div>
          ) : (
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              id={name}
              name={name}
              value={formData[name]}
              onChange={(e) => handleInputChange(name, e.target.value)}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderSelect = (name, label, options, required, onChange, disabled) => (
    <div className="col-md-6">
      <div className="form-group row">
        <label className="col-md-4 label-control col-form-label" htmlFor={name}>
          {label}
          {required ? '*' : ''}:
        </label>
        <div className="col-md-8">
          {isView ? (
            <div className="form-control-static col-form-label form-value">{labelOf(options, formData[name])}</div>
          ) : (
            <select
              className="form-control"
              id={name}
              name={name}
              value={formData[name]}
              disabled={disabled}
              onChange={(e) => (onChange ? onChange(e.target.value) : handleInputChange(name, e.target.value))}
            >
              <option value="">{intl.formatMessage({ id: 'Seleccione', defaultMessage: 'Seleccione...' })}</option>
              {options.map((option) => (
                <option key={name + '_' + option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      {redirectTo && <Redirect push to={redirectTo} />}
      {loading && <Loading />}
      <div className="row">
        <div className="col-md-12">
          <form className="form form-horizontal">
            <div className="form-body">
              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <h4 className="form-section">
                      <i className="icon-home"></i>
                      {intl.formatMessage({
                        id: 'inventarioManoObra.abm.header_datos_generales',
                        defaultMessage: 'Datos Generales',
                      })}
                      <div className="float-right" style={{ fontSize: '14px' }}>
                        *{' '}
                        {intl.formatMessage({
                          id: 'inventarioManoObra.abm.campos_requeridos',
                          defaultMessage: 'campos requeridos',
                        })}
                      </div>
                    </h4>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label">
                            {intl.formatMessage({
                              id: 'inventarioManoObra.abm.tipo_de_item',
                              defaultMessage: 'Tipo de ítem',
                            })}
                            :
                          </label>
                          <div className="col-md-8">
                            <div className="form-control-static col-form-label form-value">
                              {intl.formatMessage({
                                id: 'inventarioManoObra.abm.tipo_mano_de_obra',
                                defaultMessage: 'Mano de Obra',
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                      {renderText(
                        'numeroDeParte',
                        intl.formatMessage({ id: 'inventarioManoObra.abm.numero_de_parte', defaultMessage: 'Nº de parte' }),
                        true
                      )}
                    </div>

                    <div className="row">
                      {renderText(
                        'descripcion',
                        intl.formatMessage({ id: 'inventarioManoObra.abm.descripcion', defaultMessage: 'Descripción' }),
                        true
                      )}
                      {renderText(
                        'referencia',
                        intl.formatMessage({ id: 'inventarioManoObra.abm.referencia', defaultMessage: 'Referencia' }),
                        false
                      )}
                    </div>

                    <div className="row">
                      {renderSelect(
                        'categoria',
                        intl.formatMessage({ id: 'inventarioManoObra.abm.categoria', defaultMessage: 'Categoría' }),
                        categorias,
                        true,
                        handleCategoriaChange,
                        false
                      )}
                      {renderSelect(
                        'subcategoria',
                        intl.formatMessage({ id: 'inventarioManoObra.abm.subcategoria', defaultMessage: 'Subcategoría' }),
                        subcategorias,
                        true,
                        null,
                        !formData.categoria
                      )}
                    </div>

                    <div className="row">
                      {renderNumber(
                        'costo',
                        intl.formatMessage({ id: 'inventarioManoObra.abm.costo', defaultMessage: 'Costo' }),
                        false
                      )}
                      {renderNumber(
                        'valorMaximo',
                        intl.formatMessage({ id: 'inventarioManoObra.abm.valor_maximo', defaultMessage: 'Valor máximo' }),
                        false
                      )}
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="costoFijo">
                            {intl.formatMessage({ id: 'inventarioManoObra.abm.costo_fijo', defaultMessage: 'Costo fijo' })}
                          </label>
                          <div className="col-md-8">
                            <Switch
                              onChange={(estado) => handleInputChange('costoFijo', estado)}
                              checked={formData.costoFijo ? true : false}
                              id="costoFijo"
                              name="costoFijo"
                              disabled={isView}
                              offColor="#FF4961"
                              onColor="#28D094"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="activo">
                            {intl.formatMessage({ id: 'inventarioManoObra.abm.activo', defaultMessage: 'Activo' })}
                          </label>
                          <div className="col-md-8">
                            <Switch
                              onChange={(estado) => handleInputChange('activo', estado)}
                              checked={formData.activo ? true : false}
                              id="activo"
                              name="activo"
                              disabled={
                                isView ||
                                (isEdit && !Security.hasPermission('INVENTARIO_MANO_OBRA_DESACTIVAR'))
                              }
                              offColor="#FF4961"
                              onColor="#28D094"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12">
                        <small className="text-muted">
                          <FormattedMessage
                            id="inventarioManoObra.abm.ayuda_valor_maximo"
                            defaultMessage="Valor máximo: tope del costo de este ítem. Dejarlo vacío significa sin tope."
                          />
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <div className="text-cd text-right">
                      {!isView && (
                        <button onClick={handleSubmit} className="btn btn-primary mr-1">
                          <i className="fa fa-check-circle"></i>{' '}
                          {intl.formatMessage({ id: 'inventarioManoObra.abm.guardar', defaultMessage: 'Guardar' })}
                        </button>
                      )}
                      <button type="button" className="btn btn-danger" onClick={() => exit()}>
                        <i className="fa fa-times-circle"></i>{' '}
                        {isView
                          ? intl.formatMessage({ id: 'inventarioManoObra.abm.volver', defaultMessage: 'Volver' })
                          : intl.formatMessage({ id: 'inventarioManoObra.abm.cancelar', defaultMessage: 'Cancelar' })}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default InventarioManoObraAbm;
